import {
  RELATION__ACCOUNTS_STARRED,
  RELATION__CANDIDATES_STARRED,
  RELATION__EMPLOYERS_STARRED,
  RELATION__ENGAGEMENTS_STARRED,
  RELATION__JOBS_STARRED
} from '../../dictionaries/Relations.dic';

/**
 * Orders JSON object properties alphabetically, placing specified keys at the front,
 * specified keys at the end (before underscore-prefixed keys),
 * and keys starting with '_' at the very end.
 * Optionally sorts middle keys grouped by value type in a custom order.
 *
 * @param {Object|Object[]} data - JSON object or array of JSON objects to reorder.
 * @param {string[]} prioritizedKeys - Keys to place at the beginning in order.
 * @param {string[]} keysAtEnd - Keys to place at the end before underscore-prefixed keys.
 * @param {boolean} enableTypeSorting - Whether to group and sort middle keys by type.
 * @param {string[]} typeSortOrder - Custom order for value types when enableTypeSorting is true.
 * @returns {Object|Object[]} - New JSON object or array with reordered properties.
 */
export default function orderJsonProperties({
  data = [],
  prioritizedKeys = [
    'id',
    'firstName',
    'lastName',
    'name',
    'jobTitle',
    'email',
    'currentEmployer',
    'currentTitle',
    'undergraduateSchool',
    'phone',
    'state',
    'status',
    'stage',
    'emailsList',
    'positiveSignals',
    'negativeSignals',
    'account',
    'accountEmployer',
    'candidate',
    'employer',
    'job',
    'engagements',
  ],
  keysAtEnd = [
    RELATION__ACCOUNTS_STARRED,
    RELATION__CANDIDATES_STARRED,
    RELATION__EMPLOYERS_STARRED,
    RELATION__JOBS_STARRED,
    RELATION__ENGAGEMENTS_STARRED,
    'createdAt',
    'updatedAt'
  ],
  enableTypeSorting = true,
  typeSortOrder = [
    'null',
    'boolean',
    'number',
    'string',
    'object',
    'array',
    'other'
  ],
}) {
  // Helper function to order a single object
  function orderObject(obj) {
    const ordered = {};

    // Add prioritized keys first
    for (const key of prioritizedKeys) {
      if (key in obj) {
        ordered[key] = obj[key];
      }
    }

    const keys = Object.keys(obj);

    const endKeys = keysAtEnd
      .filter(key => keys.includes(key) && !prioritizedKeys.includes(key))
      .sort();

    const underscoreKeys = keys
      .filter(key => key.startsWith('_') && !prioritizedKeys.includes(key) && !keysAtEnd.includes(key))
      .sort();

    const normalKeys = keys.filter(
      key =>
        !prioritizedKeys.includes(key) &&
        !keysAtEnd.includes(key) &&
        !key.startsWith('_')
    );

    let sortedMiddleKeys;

    if (enableTypeSorting) {
      const typeBuckets = {
        null: [],
        boolean: [],
        number: [],
        string: [],
        object: [],
        array: [],
        other: [],
      };

      for (const key of normalKeys) {
        const value = obj[key];

        if (value === null) {
          typeBuckets.null.push(key);
        } else if (typeof value === 'boolean') {
          typeBuckets.boolean.push(key);
        } else if (typeof value === 'number') {
          typeBuckets.number.push(key);
        } else if (typeof value === 'string') {
          typeBuckets.string.push(key);
        } else if (Array.isArray(value)) {
          typeBuckets.array.push(key);
        } else if (typeof value === 'object') {
          typeBuckets.object.push(key);
        } else {
          typeBuckets.other.push(key);
        }
      }

      sortedMiddleKeys = typeSortOrder.flatMap(type =>
        typeBuckets[type]?.sort() ?? []
      );
    } else {
      sortedMiddleKeys = normalKeys.sort();
    }

    for (const key of sortedMiddleKeys) {
      ordered[key] = obj[key];
    }

    for (const key of endKeys) {
      ordered[key] = obj[key];
    }

    for (const key of underscoreKeys) {
      ordered[key] = obj[key];
    }

    return ordered;
  }

  if (Array.isArray(data)) {
    return data.map(orderObject);
  } else if (typeof data === 'object' && data !== null) {
    return orderObject(data);
  } else {
    return data;
  }
}
